import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Play, Upload, Save, Loader2, Video, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminVideoSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    client_welcome_video_url: '',
    client_welcome_video_title: 'Welcome to iEvntür',
    client_welcome_video_description: '',
    talent_welcome_video_url: '',
    talent_welcome_video_title: 'Welcome to iEvntür',
    talent_welcome_video_description: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings/welcome-video');
      if (res.data) {
        setSettings(prev => ({
          ...prev,
          ...res.data
        }));
      }
    } catch (error) {
      toast.error('Failed to load video settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each video setting individually
      const saves = [];
      if (settings.talent_welcome_video_url) {
        saves.push(api.put('/admin/settings/welcome-video', { key: 'talent_welcome_video_url', video_url: settings.talent_welcome_video_url }));
      }
      if (settings.client_welcome_video_url) {
        saves.push(api.put('/admin/settings/welcome-video', { key: 'client_welcome_video_url', video_url: settings.client_welcome_video_url }));
      }
      // Save titles as separate settings
      if (settings.talent_welcome_video_title) {
        saves.push(api.put('/admin/settings/welcome-video', { key: 'talent_welcome_video_title', video_url: settings.talent_welcome_video_title }));
      }
      if (settings.client_welcome_video_title) {
        saves.push(api.put('/admin/settings/welcome-video', { key: 'client_welcome_video_title', video_url: settings.client_welcome_video_title }));
      }
      await Promise.all(saves);
      toast.success('Video settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be less than 100MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    toast.loading('Uploading video...', { id: 'video-upload' });

    try {
      const res = await api.post('/admin/settings/upload-video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      
      if (res.data.video_url) {
        const fieldName = type === 'client' ? 'client_welcome_video_url' : 'talent_welcome_video_url';
        setSettings(prev => ({ ...prev, [fieldName]: res.data.video_url }));
        toast.dismiss('video-upload');
        toast.success(`Video uploaded (${res.data.size_mb}MB)`);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.dismiss('video-upload');
      toast.error('Failed to upload video. You can also paste a direct URL instead.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-video-settings">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold">Welcome Video Settings</h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Client Welcome Video */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-green-600" />
              Client Welcome Video
            </CardTitle>
            <CardDescription>
              This video is shown to new clients when they first log in
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Video URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={settings.client_welcome_video_url}
                      onChange={(e) => setSettings(prev => ({ ...prev, client_welcome_video_url: e.target.value }))}
                      placeholder="https://... or upload below"
                      data-testid="client-video-url"
                    />
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept="video/*" 
                        className="hidden" 
                        onChange={(e) => handleUpload('client', e)}
                      />
                      <Button variant="outline" asChild>
                        <span><Upload className="w-4 h-4 mr-2" />Upload</span>
                      </Button>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={settings.client_welcome_video_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, client_welcome_video_title: e.target.value }))}
                    placeholder="Welcome to iEvntür"
                    data-testid="client-video-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={settings.client_welcome_video_description}
                    onChange={(e) => setSettings(prev => ({ ...prev, client_welcome_video_description: e.target.value }))}
                    placeholder="A brief description shown below the video..."
                    rows={3}
                    data-testid="client-video-description"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Preview</Label>
                {settings.client_welcome_video_url ? (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video 
                      src={settings.client_welcome_video_url} 
                      controls 
                      className="w-full h-full"
                      data-testid="client-video-preview"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <p className="text-gray-400 text-sm">No video set</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Talent Welcome Video */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-cyan-600" />
              Talent Welcome Video
            </CardTitle>
            <CardDescription>
              This video is shown to new talent when they first log in
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Video URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={settings.talent_welcome_video_url}
                      onChange={(e) => setSettings(prev => ({ ...prev, talent_welcome_video_url: e.target.value }))}
                      placeholder="https://... or upload below"
                      data-testid="talent-video-url"
                    />
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept="video/*" 
                        className="hidden" 
                        onChange={(e) => handleUpload('talent', e)}
                      />
                      <Button variant="outline" asChild>
                        <span><Upload className="w-4 h-4 mr-2" />Upload</span>
                      </Button>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={settings.talent_welcome_video_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, talent_welcome_video_title: e.target.value }))}
                    placeholder="Welcome to iEvntür"
                    data-testid="talent-video-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={settings.talent_welcome_video_description}
                    onChange={(e) => setSettings(prev => ({ ...prev, talent_welcome_video_description: e.target.value }))}
                    placeholder="A brief description shown below the video..."
                    rows={3}
                    data-testid="talent-video-description"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Preview</Label>
                {settings.talent_welcome_video_url ? (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video 
                      src={settings.talent_welcome_video_url} 
                      controls 
                      className="w-full h-full"
                      data-testid="talent-video-preview"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <p className="text-gray-400 text-sm">No video set</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <h3 className="font-medium text-blue-900 mb-2">Tips for Welcome Videos</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Keep videos under 2 minutes for best engagement</li>
              <li>• Supported formats: MP4, WebM, MOV</li>
              <li>• Maximum file size: 100MB</li>
              <li>• You can also use YouTube or Vimeo embed URLs</li>
              <li>• Changes will appear immediately after saving</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminVideoSettings;
